"use client";

import Link from "next/link";
import Image from "next/image";
import { useState } from "react";
import { Menu, X, ChevronDown, ArrowRight, Search } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import { courses as allCourses } from "@/lib/courses";

type DropdownItem = {
  name: string;
  href: string;
  badge?: string;
  color?: string;
};

type DropdownMenu = {
  key: "courses" | "teams";
  label: string;
  href: string;
  items: DropdownItem[];
};

const courses: DropdownItem[] = allCourses.map((c) => ({
  name: c.name,
  href: `/courses/${c.slug}`,
  badge: c.cert,
  color: c.color,
}));

const teamsItems: DropdownItem[] = [
  { name: "CloudBreach+ Subscription", href: "/teams#plans" },
  { name: "ProofRange", href: "/proofrange" },
];

const coursesMenu: DropdownMenu = { key: "courses", label: "Courses", href: "/courses", items: courses };
const teamsMenu: DropdownMenu = { key: "teams", label: "Teams", href: "/teams", items: teamsItems };

// Order of the desktop nav: Courses ▾, Labs, Certifications, Teams ▾, Blog, Contact
const navItems: Array<{ label: string; href: string } | DropdownMenu> = [
  coursesMenu,
  { label: "Labs", href: "/labs" },
  { label: "Certifications", href: "/certifications" },
  teamsMenu,
  { label: "Blog", href: "/blog" },
  { label: "Contact", href: "/contact-us" },
];

const isDropdown = (item: (typeof navItems)[number]): item is DropdownMenu => "items" in item;

function DropdownLink({ item, onClick, className }: { item: DropdownItem; onClick?: () => void; className?: string }) {
  return (
    <Link
      href={item.href}
      className={cn(
        "flex items-center justify-between px-3 py-2.5 rounded-lg text-sm text-cb-muted hover:text-cb-text hover:bg-cb-surface-2 transition-colors",
        className
      )}
      onClick={onClick}
    >
      <span>{item.name}</span>
      {item.badge && (
        <span
          className="text-xs font-mono px-1.5 py-0.5 rounded"
          style={{ color: item.color, backgroundColor: `${item.color}18` }}
        >
          {item.badge}
        </span>
      )}
    </Link>
  );
}

export function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<DropdownMenu["key"] | null>(null);

  return (
    <header className="sticky top-0 z-50 border-b border-cb-border bg-cb-bg/90 backdrop-blur-md">
      <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between">
          {/* Logo */}
          <Link href="/" className="flex items-center">
            <Image
              src="/CloudBreach_logo.png"
              alt="CloudBreach"
              width={180}
              height={45}
              sizes="180px"
              className="h-10 w-auto"
              style={{ mixBlendMode: "screen" }}
              priority
            />
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-1">
            {navItems.map((item) =>
              isDropdown(item) ? (
                <div
                  key={item.key}
                  className="relative"
                  onMouseEnter={() => setOpenMenu(item.key)}
                  onMouseLeave={() => setOpenMenu(null)}
                >
                  <Link
                    href={item.href}
                    aria-haspopup="menu"
                    aria-expanded={openMenu === item.key}
                    className={cn(
                      "flex items-center gap-1 px-3 py-2 rounded-md text-base font-medium transition-colors",
                      "text-cb-muted hover:text-cb-text hover:bg-cb-surface-2"
                    )}
                    onFocus={() => setOpenMenu(item.key)}
                  >
                    {item.label}
                    <ChevronDown
                      className={cn(
                        "h-3.5 w-3.5 transition-transform duration-150",
                        openMenu === item.key && "rotate-180"
                      )}
                    />
                  </Link>
                  {openMenu === item.key && (
                    <div className="absolute left-0 top-full pt-1" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOpenMenu(null); }}>
                      <div className="w-64 rounded-xl border border-cb-border bg-cb-surface p-1.5 shadow-xl shadow-black/40">
                        {item.items.map((sub) => (
                          <DropdownLink key={sub.href} item={sub} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={item.href}
                  href={item.href}
                  className="px-3 py-2 rounded-md text-base font-medium text-cb-muted hover:text-cb-text hover:bg-cb-surface-2 transition-colors"
                >
                  {item.label}
                </Link>
              )
            )}
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <Link
              href="/search"
              aria-label="Search the site"
              className="flex h-11 w-11 items-center justify-center rounded-md border border-cb-border text-cb-muted hover:text-cb-text hover:border-cb-violet/40 transition-colors"
            >
              <Search className="h-4 w-4" />
            </Link>
            <a
              href="https://portal.cloudbreach.io/sign-in/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-sm font-medium text-cb-muted hover:text-cb-text border border-cb-border hover:border-cb-violet/40 px-5 py-2.5 rounded-md transition-all duration-150 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cb-violet/50"
            >
              Sign in
            </a>
            <Button asChild size="md">
              <Link href="/courses" className="flex items-center gap-2">
                Start Training
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </Button>
          </div>

          {/* Mobile actions */}
          <div className="md:hidden flex items-center gap-1">
            <Link
              href="/search"
              aria-label="Search the site"
              className="flex h-11 w-11 items-center justify-center rounded-md text-cb-muted hover:text-cb-text transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cb-violet/50"
              onClick={() => setMobileOpen(false)}
            >
              <Search className="h-5 w-5" />
            </Link>
            <button
              className="flex h-11 w-11 items-center justify-center rounded-md text-cb-muted hover:text-cb-text transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cb-violet/50"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden border-t border-cb-border py-4 space-y-1 max-h-[calc(100dvh-5rem)] overflow-y-auto overscroll-contain pb-[max(1rem,env(safe-area-inset-bottom))]">
            {navItems.map((item) =>
              isDropdown(item) ? (
                <div key={item.key} className="px-2 pb-2">
                  <Link
                    href={item.href}
                    className="block px-3 py-2.5 rounded-lg text-sm font-semibold text-cb-muted-2 uppercase tracking-wider hover:text-cb-text hover:bg-cb-surface-2 transition-colors"
                    onClick={() => setMobileOpen(false)}
                  >
                    {item.label}
                  </Link>
                  {item.items.map((sub) => (
                    <DropdownLink key={sub.href} item={sub} onClick={() => setMobileOpen(false)} />
                  ))}
                </div>
              ) : (
                <Link
                  key={item.href}
                  href={item.href}
                  className="block px-3 py-2.5 rounded-lg text-sm text-cb-muted hover:text-cb-text hover:bg-cb-surface-2 transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  {item.label}
                </Link>
              )
            )}
            <div className="pt-3 px-2 border-t border-cb-border mt-3 space-y-2">
              <a
                href="https://portal.cloudbreach.io/sign-in/"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full text-center px-4 py-2.5 rounded-lg text-sm text-cb-muted border border-cb-border hover:border-cb-violet/40 hover:text-cb-text transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                Sign in
              </a>
              <Button className="w-full" asChild>
                <Link href="/courses" className="flex items-center gap-2">
                  Start Training
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
